import React, { useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { ITemplateSocietario, TipoOperacaoSocietario } from '../../../../hooks/useSocietario';

// Sessao 2026-05-15 (review final P0 A5): cap 50MB alinhado com os demais
// modais de upload. Antes este modal aceitava qualquer tamanho.
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

interface AtualizarTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  tipoOperacao: TipoOperacaoSocietario;
  templateAtual?: ITemplateSocietario;
  // eslint-disable-next-line no-unused-vars
  onSubmit: (data: {
    nome: string;
    versao: string;
    descricao: string;
    file: File;
  }) => Promise<void>;
  isLoading?: boolean;
}

const tipoLabel: Record<TipoOperacaoSocietario, string> = {
  constituicao: 'Constituição',
  alteracao: 'Alteração contratual',
  transformacao: 'Transformação (EI → LTDA)',
};

export const AtualizarTemplateModal = ({
  isOpen,
  onClose,
  tipoOperacao,
  templateAtual,
  onSubmit,
  isLoading,
}: AtualizarTemplateModalProps) => {
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [versao, setVersao] = useState('');
  const [descricao, setDescricao] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const reset = () => {
    setNome('');
    setVersao('');
    setDescricao('');
    setFile(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!nome.trim() || !versao.trim()) {
      toast({ status: 'warning', title: 'Informe nome e versão' });
      return;
    }
    if (!file) {
      toast({ status: 'warning', title: 'Selecione o arquivo DOCX' });
      return;
    }
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast({ status: 'error', title: 'O arquivo deve ser .docx' });
      return;
    }
    // Sessao 2026-05-15 (review final P0 A5): validar tamanho client-side.
    if (file.size > MAX_UPLOAD_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      toast({
        status: 'error',
        title: `Arquivo muito grande (${mb}MB)`,
        description: 'Limite: 50MB. Comprima o DOCX antes de tentar novamente.',
      });
      return;
    }
    // Sessao 2026-05-15 (review final P0 A6): reset() so apos sucesso do
    // onSubmit. Antes era chamado incondicionalmente apos `await` — se
    // onSubmit (que o parent catcheia e nao re-throws) falhasse, o form
    // era apagado mesmo assim e operador perdia tudo que digitou. Agora
    // se onSubmit lancar, o catch preserva o form e mostra toast.
    try {
      await onSubmit({
        nome: nome.trim(),
        versao: versao.trim(),
        descricao: descricao.trim(),
        file,
      });
      reset();
    } catch (err: any) {
      // Parent ja exibe toast — aqui so impedimos o reset() pra preservar
      // o que o operador digitou e ele possa tentar de novo.
      // eslint-disable-next-line no-console
      console.warn('Falha no onSubmit do AtualizarTemplateModal', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {templateAtual ? 'Atualizar template' : 'Cadastrar template'} — {tipoLabel[tipoOperacao]}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {templateAtual && (
            <Text fontSize="xs" color="gray.500" mb={3}>
              Ao salvar, o template atual <strong>{templateAtual.nome}</strong> (v
              {templateAtual.versao}) será desativado e o novo se torna o ativo.
            </Text>
          )}
          <Stack spacing={3}>
            <Input
              placeholder="Nome (ex: Contrato padrão LTDA)"
              value={nome}
              onChange={e => setNome(e.target.value)}
              bg="white"
            />
            <Input
              placeholder="Versão (ex: 1, 2.0, 2026-04)"
              value={versao}
              onChange={e => setVersao(e.target.value)}
              bg="white"
            />
            <Textarea
              placeholder="Descrição (opcional)"
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              rows={3}
              bg="white"
            />
            <Input
              type="file"
              accept=".docx"
              onChange={e => setFile(e.target.files?.[0] ?? null)}
              bg="white"
              p={1}
            />
            {file && (
              <Text fontSize="xs" color="gray.600">
                Selecionado: {file.name} ({Math.round(file.size / 1024)} KB)
              </Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancelar
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isLoading}>
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AtualizarTemplateModal;
